import { TextField, Box, Typography, Tooltip } from "@mui/material";
import ReplayIcon from "@mui/icons-material/Replay";
import { useState, useEffect, useCallback } from "react";
import { colors } from "../../ui/colors";

interface ResettableInputProps {
  defaultValue: number;
  value?: number;
  onChange?: (value: number | null) => void;
  onReset?: () => void;
  additionalResetStates?: Array<() => void>;
  placeholder?: string;
  label?: string;
  helperText?: string;
  size?: "small" | "medium";
  width?: string;
  align?: "left" | "right" | "center";
  fontSize?: string;
  fontWeight?: number | string;
  color?: string;
  disabled?: boolean;
  showResetButton?: boolean;
  resetTooltip?: string;
  readOnly?: boolean;
  formatValue?: (value: number) => string;
  /**
   * When the field is cleared and the user blurs, commit this numeric value.
   * If omitted, `defaultValue` is used (same as legacy "restore default on empty blur").
   */
  emptyCommitValue?: number;
}

const nearEqual = (a: number, b: number) => Math.abs(a - b) < 0.0001;

export const ResettableInput = ({
  defaultValue,
  value: controlledValue,
  onChange,
  onReset,
  additionalResetStates,
  placeholder = "",
  label,
  helperText,
  size = "small",
  width = "100%",
  align = "left",
  fontSize = "1rem",
  fontWeight = 400,
  color,
  disabled = false,
  showResetButton = true,
  readOnly = false,
  formatValue,
  resetTooltip,
  emptyCommitValue,
}: ResettableInputProps) => {
  const isControlled = controlledValue !== undefined;

  const [focused, setFocused] = useState(false);
  const [inputValue, setInputValue] = useState(String(defaultValue));
  const [lastDefaultValue, setLastDefaultValue] = useState(defaultValue);
  const [initialFocusValue, setInitialFocusValue] = useState<number | null>(null);

  const committedNumber = isControlled ? (controlledValue ?? defaultValue) : parseFloat(inputValue) || defaultValue;

  // Sync display string from parent when not actively editing (matches Qty draft behavior).
  useEffect(() => {
    if (focused) return;
    if (isControlled) {
      const v = controlledValue ?? defaultValue;
      setInputValue(String(v));
    }
  }, [controlledValue, defaultValue, focused, isControlled]);

  // Uncontrolled: follow defaultValue updates when user has not diverged (legacy).
  useEffect(() => {
    if (isControlled || focused) return;
    const currentNum = parseFloat(inputValue) || 0;
    if (nearEqual(currentNum, lastDefaultValue) && !nearEqual(defaultValue, lastDefaultValue)) {
      setInputValue(String(defaultValue));
      setLastDefaultValue(defaultValue);
    } else if (!nearEqual(defaultValue, lastDefaultValue)) {
      setLastDefaultValue(defaultValue);
    }
  }, [defaultValue, focused, inputValue, isControlled, lastDefaultValue]);

  const commitFromDraft = useCallback(() => {
    const emptyFallback = emptyCommitValue !== undefined ? emptyCommitValue : defaultValue;
    const trimmed = inputValue.trim();
    if (trimmed === "" || trimmed === ".") {
      setInputValue(String(emptyFallback));
      if (initialFocusValue === null || !nearEqual(emptyFallback, initialFocusValue)) {
        onChange?.(emptyFallback);
      }
      return;
    }
    const num = parseFloat(trimmed);
    if (Number.isNaN(num)) {
      setInputValue(String(defaultValue));
      if (initialFocusValue === null || !nearEqual(defaultValue, initialFocusValue)) {
        onChange?.(defaultValue);
      }
      return;
    }
    if (initialFocusValue === null || !nearEqual(num, initialFocusValue)) {
      onChange?.(num);
    }
  }, [defaultValue, emptyCommitValue, inputValue, initialFocusValue, onChange]);

  const handleFocus = () => {
    setFocused(true);
    const startVal = isControlled ? (controlledValue ?? defaultValue) : parseFloat(inputValue) || defaultValue;
    setInitialFocusValue(startVal);
    if (isControlled) {
      setInputValue(String(controlledValue ?? defaultValue));
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newInputValue = e.target.value;
    if (newInputValue === "" || /^\d*\.?\d*$/.test(newInputValue)) {
      setInputValue(newInputValue);
    }
  };

  const handleBlur = () => {
    setFocused(false);
    commitFromDraft();
  };

  const handleReset = () => {
    setFocused(false);
    setInputValue(String(defaultValue));
    if (onChange) {
      onChange(defaultValue);
    }
    if (onReset) {
      onReset();
    }
    if (additionalResetStates && Array.isArray(additionalResetStates)) {
      additionalResetStates.forEach((resetState) => {
        if (typeof resetState === "function") {
          resetState();
        }
      });
    }
  };

  const currentNumericValue = isControlled ? (controlledValue ?? defaultValue) : parseFloat(inputValue) || defaultValue;

  const parsedForResetCompare = focused ? parseFloat(inputValue) : currentNumericValue;
  const isDefaultValue = !Number.isNaN(parsedForResetCompare) && nearEqual(parsedForResetCompare, defaultValue);

  const showReset = showResetButton && !readOnly && !isDefaultValue;

  return (
    <Box sx={{ width, maxWidth: "100%" }}>
      {(label || showResetButton) && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-end",
            mb: 1,
            px: 0.5,
          }}
        >
          {label && (
            <Typography
              variant="body2"
              sx={{
                fontSize: "0.875rem",
                fontWeight: 700,
                color: "text.primary",
                lineHeight: 1.2,
              }}
            >
              {label}
            </Typography>
          )}
          {showResetButton && (
            <Tooltip title={resetTooltip ?? ""} enterDelay={300} disableInteractive disableHoverListener={!resetTooltip}>
              <Box
                component="button"
                type="button"
                onClick={showReset ? handleReset : undefined}
                disabled={!showReset || disabled}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                  background: "none",
                  border: "none",
                  padding: 0,
                  cursor: showReset ? "pointer" : "not-allowed",
                  color: showReset ? colors.orange[500] : "rgba(var(--fx-surface-textrgb, 15, 23, 42), 0.26)",
                  "&:hover": showReset
                    ? {
                        color: colors.orange[700],
                      }
                    : {},
                  "&:disabled": {
                    cursor: "not-allowed",
                  },
                  transition: "color 0.2s ease",
                  marginLeft: "auto",
                  mb: -1,
                }}
              >
                <Typography
                  sx={{
                    fontSize: "0.625rem",
                    fontWeight: 600,
                    textTransform: "uppercase",
                    letterSpacing: "0.05em",
                  }}
                >
                  Reset
                </Typography>
                <ReplayIcon sx={{ fontSize: "14px" }} />
              </Box>
            </Tooltip>
          )}
        </Box>
      )}

      <Box sx={{ position: "relative" }}>
        {readOnly ? (
          <Typography
            sx={{
              fontSize,
              fontWeight,
              color: color || "inherit",
              textAlign: align,
              width: "100%",
              display: "block",
            }}
          >
            {formatValue ? formatValue(currentNumericValue) : inputValue}
          </Typography>
        ) : (
          <TextField
            type="text"
            size={size}
            value={inputValue}
            onFocus={handleFocus}
            onChange={handleChange}
            onBlur={handleBlur}
            disabled={disabled}
            placeholder={placeholder}
            fullWidth
            inputProps={{
              style: {
                textAlign: align,
                fontSize,
                fontWeight,
                color: color || "inherit",
              },
            }}
            sx={{
              width: "100%",
              "& .MuiOutlinedInput-root": {
                bgcolor: "grey.50",
                borderRadius: "0.5rem",
                height: size === "small" ? "40px" : "56px",
                "& fieldset": {
                  borderColor: "grey.300",
                  borderWidth: "1px",
                },
                "&:hover fieldset": {
                  borderColor: colors.orange[400],
                },
                "&.Mui-focused fieldset": {
                  borderColor: colors.orange[500],
                  borderWidth: "2px",
                },
                "&.Mui-disabled": {
                  bgcolor: "grey.100",
                  "& fieldset": {
                    borderColor: "grey.200",
                  },
                },
              },
              "& .MuiOutlinedInput-input": {
                padding: size === "small" ? "10px 14px" : "14px 16px",
              },
            }}
          />
        )}
      </Box>

      {helperText && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.5,
            mt: 1,
            px: 0.5,
          }}
        >
          <Typography
            variant="caption"
            sx={{
              fontSize: "0.875rem",
              color: "text.secondary",
              fontWeight: 400,
              lineHeight: 1.5,
            }}
          >
            {helperText}
          </Typography>
        </Box>
      )}
    </Box>
  );
};
